import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { VeterinaireAvecDistance, PositionGPS } from '../../types';
import { Box, Typography, Button, CircularProgress } from '@mui/material';

// Fix pour les icônes par défaut de Leaflet

let DefaultIcon = L.divIcon({
  className: 'custom-div-icon',
  html: `<div style="
    background-color: #2196F3;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

let VetIcon = L.divIcon({
  className: 'custom-vet-icon',
  html: `<div style="
    background-color: #4CAF50;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 14px;
  ">+</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface CarteVeterinairesProps {
  veterinaires: VeterinaireAvecDistance[];
  positionUtilisateur: PositionGPS;
  onVeterinaireClick: (veterinaire: VeterinaireAvecDistance) => void;
  rayon?: number;
  loading?: boolean;
  onLoadVeterinaires?: (bounds: L.LatLngBounds) => Promise<void>;
}

// Composant pour gérer les événements de la carte
interface MapEventHandlerProps {
  onLoadVeterinaires?: (bounds: L.LatLngBounds) => Promise<void>;
}

const MapEventHandler: React.FC<MapEventHandlerProps> = ({ onLoadVeterinaires }) => {
  const map = useMap();
  const [isLoading, setIsLoading] = useState(false);

  useMapEvents({
    moveend: async () => {
      if (onLoadVeterinaires && !isLoading) {
        setIsLoading(true);
        try {
          const bounds = map.getBounds();
          await onLoadVeterinaires(bounds);
        } catch (error) {
          console.error('Erreur lors du chargement des vétérinaires:', error);
        } finally {
          setIsLoading(false);
        }
      }
    },
    zoomend: async () => {
      if (onLoadVeterinaires && !isLoading) {
        setIsLoading(true);
        try {
          const bounds = map.getBounds();
          await onLoadVeterinaires(bounds);
        } catch (error) {
          console.error('Erreur lors du chargement des vétérinaires:', error);
        } finally {
          setIsLoading(false);
        }
      }
    }
  });

  return null;
};

// Composant pour ajuster automatiquement la vue de la carte
interface MapViewControllerProps {
  veterinaires: VeterinaireAvecDistance[];
  positionUtilisateur: PositionGPS;
  rayon?: number;
}

const MapViewController: React.FC<MapViewControllerProps> = ({ 
  veterinaires, 
  positionUtilisateur, 
  rayon 
}) => {
  const map = useMap();

  useEffect(() => {
    if (veterinaires.length > 0) {
      // Créer un groupe de toutes les positions
      const group = new L.FeatureGroup();
      
      // Ajouter la position utilisateur
      group.addLayer(L.marker([positionUtilisateur.latitude, positionUtilisateur.longitude]));
      
      // Ajouter tous les vétérinaires
      veterinaires.forEach(vet => {
        if (vet.clinique?.latitude && vet.clinique?.longitude) {
          group.addLayer(L.marker([
            parseFloat(vet.clinique.latitude.toString()),
            parseFloat(vet.clinique.longitude.toString())
          ]));
        }
      });
      
      // Ajuster la vue pour inclure tous les marqueurs avec un padding
      map.fitBounds(group.getBounds(), { padding: [20, 20] });
    } else if (rayon) {
      // Si pas de vétérinaires mais rayon défini, centrer sur l'utilisateur avec le rayon
      const center = new L.LatLng(positionUtilisateur.latitude, positionUtilisateur.longitude);
      map.setView(center, 12);
    }
  }, [veterinaires, positionUtilisateur, rayon, map]);

  return null;
};

export const CarteVeterinaires: React.FC<CarteVeterinairesProps> = ({
  veterinaires,
  positionUtilisateur,
  onVeterinaireClick,
  rayon = 10,
  loading = false,
  onLoadVeterinaires
}) => {
  const mapRef = useRef<L.Map | null>(null);

  const rayonEnMetres = rayon * 1000; // Convertir km en mètres

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="400px"
        bgcolor="grey.100"
        borderRadius={1}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <MapContainer
        center={[positionUtilisateur.latitude, positionUtilisateur.longitude]}
        zoom={12}
        style={{ height: '400px', width: '100%', borderRadius: '8px' }}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {/* Gestionnaire d'événements pour le chargement dynamique */}
        {onLoadVeterinaires && (
          <MapEventHandler onLoadVeterinaires={onLoadVeterinaires} />
        )}

        {/* Contrôleur de vue automatique */}
        <MapViewController
          veterinaires={veterinaires}
          positionUtilisateur={positionUtilisateur}
          rayon={rayon}
        />

        {/* Marqueur de position utilisateur */}
        <Marker
          position={[positionUtilisateur.latitude, positionUtilisateur.longitude]}
          icon={DefaultIcon}
        >
          <Popup>
            <div style={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" gutterBottom>
                📍 Votre position
              </Typography>
              <Typography variant="body2">
                Latitude: {positionUtilisateur.latitude.toFixed(4)}
              </Typography>
              <Typography variant="body2">
                Longitude: {positionUtilisateur.longitude.toFixed(4)}
              </Typography>
            </div>
          </Popup>
        </Marker>

        {/* Cercle de rayon (optionnel) */}
        {rayon && !onLoadVeterinaires && (
          <Circle
            center={[positionUtilisateur.latitude, positionUtilisateur.longitude]}
            radius={rayonEnMetres}
            pathOptions={{
              color: '#2196F3',
              fillColor: '#2196F3',
              fillOpacity: 0.1,
              weight: 2,
              dashArray: '5, 5'
            }}
          />
        )}

        {/* Marqueurs des vétérinaires */}
        {veterinaires.map((veterinaire) => {
          if (!veterinaire.clinique?.latitude || !veterinaire.clinique?.longitude) {
            return null;
          }

          const lat = parseFloat(veterinaire.clinique.latitude.toString());
          const lng = parseFloat(veterinaire.clinique.longitude.toString());

          return (
            <Marker
              key={veterinaire.id}
              position={[lat, lng]}
              icon={VetIcon}
              eventHandlers={{
                click: () => onVeterinaireClick(veterinaire),
              }}
            >
              <Popup>
                <div style={{ minWidth: '200px' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Dr. {veterinaire.prenom} {veterinaire.nom}</strong>
                  </Typography>
                  
                  {veterinaire.specialite && (
                    <Typography variant="body2" color="primary" gutterBottom>
                      {veterinaire.specialite}
                    </Typography>
                  )}

                  {veterinaire.clinique && (
                    <Box mb={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {veterinaire.clinique.nom}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {veterinaire.clinique.ville}
                      </Typography>
                    </Box>
                  )}

                  {veterinaire.distance && (
                    <Typography variant="body2" color="success.main" gutterBottom>
                      📍 À {veterinaire.distance.toFixed(1)} km
                    </Typography>
                  )}

                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={() => onVeterinaireClick(veterinaire)}
                    sx={{ mt: 1 }}
                  >
                    Voir détails
                  </Button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Indicateur du nombre de vétérinaires */}
      <Box mt={2} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {veterinaires.length > 0 
            ? `${veterinaires.length} vétérinaire${veterinaires.length > 1 ? 's' : ''} affiché${veterinaires.length > 1 ? 's' : ''} sur la carte`
            : 'Aucun vétérinaire dans cette zone'
          }
          {onLoadVeterinaires && (
            <span> • Déplacez la carte pour découvrir d'autres vétérinaires</span>
          )}
        </Typography>
      </Box>
    </Box>
  );
}; 